import { Button, Collapse, Progress, Space, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import Breadcrumb from 'components/Breadcrumb';
import styles from './PublishingRequirementsPage.module.less';
import { useEffect, useMemo, useRef, useState } from 'react';
import api, {
	ProductPublishingRequirementRecordDto,
	PublishingRequirementCategoryV2,
} from 'api';
import { IRoutes } from 'components/Breadcrumb/Breadcrumb';
import { useSelector } from 'react-redux';
import { projectSelector } from 'redux/selectors/projectSelector';
import OmniNotification from 'components/OmniNotification';
import RequirementItem from './RequirementItem';
import _ from 'lodash';
import { Prompt, useHistory } from 'react-router-dom';
import treadmillIcon from 'assets/img/treadmill.svg';
import controllerIcon from 'assets/img/controller.svg';
import {
	DashboardOutlined,
	SecurityScanOutlined,
	DeploymentUnitOutlined,
	FileProtectOutlined,
	ReadOutlined,
	ContainerOutlined,
	ShopOutlined,
} from '@ant-design/icons';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import RouteLeavingGuard from 'components/RouteLeavingGuard';
import { confirmResultType } from 'components/RouteLeavingGuard/RouteLeavingGuard';
const { Panel } = Collapse;

interface ProductPublishingRequirementRecord
	extends ProductPublishingRequirementRecordDto {
	testId?: string;
}

export default function PublishingRequirementsPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const projectInfo = useSelector(projectSelector);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [
		shouldCallSubmitWhenNavigatingAway,
		setShouldCallSubmitWhenNavigatingAway,
	] = useState(false);
	const [confirmResult, setConfirmResult] = useState<confirmResultType>({
		loading: false,
		result: false,
	});
	const manuallySave = useRef(false);

	const [items, setItems] = useState<ProductPublishingRequirementRecord[]>([]);
	const [originalItems, setOriginalItems] = useState<
		ProductPublishingRequirementRecord[]
	>([]);
	const [completedItems, setCompletedItems] = useState(0);
	const [totalItems, setTotalItems] = useState(0);
	const [progress, setProgress] = useState(0);
	const [categories, setCategories] = useState<
		PublishingRequirementCategoryV2[]
	>([]);

	useEffect(() => {
		fetchItems();
	}, [projectInfo?.productId]);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: 'publishing-requirements',
				breadcrumbName: t('publishingRequirementsPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	useEffect(() => {
		const completedCount = items.filter((item) => item.isCompleted).length;
		const totalCount = items.length;
		const progress = !!totalCount
			? Math.round((completedCount / totalCount) * 100)
			: 0;
		setCompletedItems(completedCount);
		setProgress(progress);
	}, [items]);

	const fetchItems = async () => {
		if (!projectInfo?.productId) return;
		setIsLoading(true);
		try {
			const items = await api.getProductPublishingRequirements(
				projectInfo?.productId,
			);
			const mappedItems = items.map<ProductPublishingRequirementRecord>(
				(item) => {
					return {
						...item,
						isCompleted: !!item.isCompleted,
						description: t(
							`publishingRequirementsPage.item.${item.description}`,
						),
						prefix:
							item.prefix &&
							t(`publishingRequirementsPage.prefix.${item.prefix}`),
						testId: `${PublishingRequirementCategoryV2[item.categoryId!]}.${item.description}`,
					};
				},
			);
			setOriginalItems(_.cloneDeep(mappedItems));
			setTotalItems(mappedItems.length);
			setItems(mappedItems);
			setCategories(_.uniq(mappedItems.map((item) => item.categoryId!)));
		} catch (error: any) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: error.message || t('api.defaultError'),
			});
		} finally {
			setIsLoading(false);
		}
	};

	const submit = async () => {
		setIsSubmitting(true);
		if (!manuallySave.current) {
			setConfirmResult({
				loading: true,
				result: false,
			});
		}
		try {
			await api.upsertProductPublishingRequirements(projectInfo?.productId!, {
				requirementItems: items.map((item) => ({
					itemId: item.itemId,
					isCompleted: item.isCompleted,
				})),
			});
			setOriginalItems(_.cloneDeep(items));

			const isCompleted = items.every((item) => item.isCompleted);
			const message = isCompleted
				? t('publishingRequirementsPage.completeSuccess.message')
				: t('publishingRequirementsPage.saveSuccess.message');
			const description = isCompleted
				? t('publishingRequirementsPage.completeSuccess.description')
				: t('publishingRequirementsPage.saveSuccess.description');
			OmniNotification.success({
				message,
				description,
			});
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: true,
				});
			}
		} catch (error: any) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: error.message || t('api.defaultError'),
			});
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: false,
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCheckChange = (id: number, checked: boolean) => {
		const newItems = items.map((item) => {
			if (item.itemId === id) {
				item.isCompleted = checked;
			}
			return item;
		});
		setItems(newItems);
	};
	const getCategoryIcon = (categoryId: PublishingRequirementCategoryV2) => {
		switch (categoryId) {
			case PublishingRequirementCategoryV2.PlayerMovement:
				return (
					<img className={styles.categoryIcon} alt="" src={treadmillIcon} />
				);
			case PublishingRequirementCategoryV2.Performance:
				return <DashboardOutlined className={styles.categoryIcon} />;
			case PublishingRequirementCategoryV2.Security:
				return <SecurityScanOutlined className={styles.categoryIcon} />;
			case PublishingRequirementCategoryV2.OnlineFeatures:
			case PublishingRequirementCategoryV2.MultiplayerFeatures:
				return <DeploymentUnitOutlined className={styles.categoryIcon} />;
			case PublishingRequirementCategoryV2.VRInputFunctionality:
				return (
					<img className={styles.categoryIcon} alt="" src={controllerIcon} />
				);
			case PublishingRequirementCategoryV2.PublishingRequirements:
				return <ReadOutlined className={styles.categoryIcon} />;
			default:
				return <></>;
		}
	};
	const ifItemsChanged = useMemo(() => {
		return !_.isEqual(items, originalItems);
	}, [items, originalItems]);

	useEffect(() => {
		if (shouldCallSubmitWhenNavigatingAway) {
			manuallySave.current = false;
			submit();
		}
	}, [shouldCallSubmitWhenNavigatingAway]);

	return (
		<>
			<RouteLeavingGuard
				when={ifItemsChanged}
				confirmResult={confirmResult!}
				confirmHandler={() => {
					setShouldCallSubmitWhenNavigatingAway(true);
				}}
				isFormValid={true}
			/>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" data-testid="page-title">
							{t('publishingRequirementsPage.header')}
						</h2>
					</div>
					<Space size={[8, 16]} wrap>
						<span>
							<span className={styles.textBolder}>
								{t('publishingRequirementsPage.completed', { progress })}
							</span>
							<span> {`[${completedItems}/${totalItems}]`}</span>
						</span>
						<Progress
							strokeLinecap="butt"
							percent={progress}
							showInfo={false}
							className={styles.progress}
							strokeColor="#82DC36"
						/>

						<Button
							type="primary"
							onClick={() => {
								manuallySave.current = true;
								submit();
							}}
							loading={confirmResult.loading}
							disabled={!ifItemsChanged}
						>
							{t('publishingRequirementsPage.save')}
						</Button>
						<Button
							type="primary"
							onClick={() =>
								downloadFileByObjectURL(
									'https://docs.virtuix.com/img/O1%20Publishing%20Requirements%20Checklist%20v2.0.pdf',
								)
							}
						>
							{t('common.exportAsPdf')}
						</Button>
					</Space>
				</div>

				{isLoading ? (
					<Spin />
				) : (
					<Collapse defaultActiveKey={categories} expandIconPosition="right">
						{categories.map((categoryId) => {
							const categoryItems = items.filter(
								(item) => item.categoryId == categoryId,
							);
							const totalCount = categoryItems.length;
							const completedCount = categoryItems.filter(
								(item) => item.isCompleted,
							).length;
							return (
								<Panel
									key={categoryId}
									header={
										<>
											{getCategoryIcon(categoryId)}
											<span className={styles.panelText}>
												{`[${completedCount}/${totalCount}]`}{' '}
												{t(
													`publishingRequirementsPage.category.${PublishingRequirementCategoryV2[categoryId]}`,
												)}
											</span>
										</>
									}
								>
									{categoryItems.map((item, index) => (
										<RequirementItem
											id={item.itemId!}
											key={item.itemId}
											index={index + 1}
											description={item.description || ''}
											prefix={item.prefix || ''}
											checked={!!item.isCompleted}
											onChange={(id: number, checked: boolean) => {
												handleCheckChange(id, checked);
											}}
											testId={item.testId}
										/>
									))}
								</Panel>
							);
						})}
					</Collapse>
				)}
			</div>
		</>
	);
}
